"use client";

import { useRouter } from "@bprogress/next/app";
import { Box, ButtonBase, colors, Divider, Grid2 as Grid, IconButton, Link as MuiLink, SwipeableDrawer, Typography } from "@mui/material";
import { uiColors } from "@v2/lib/color";
import dayjs from '@v2/lib/dayjs';
import { ArrowRight, ChevronLeft, MenuIcon, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { Fragment, ReactNode, useCallback, useMemo, useState } from "react";
import LinkWithoutPrefetch from "../LinkWithoutPrefetch";
import Media from "../Media";
import MediaV2 from "../MediaV2";
import CenterBox from "../styled/CenterBox";
import FlexBox from "../styled/FlexBox";
import { MenuItemType, SubMenuItem } from "./types";
import { normalizeMenuPath } from "./utils";

export default function MobileMenu(props: {
    onChangeSubmenuIsOpen?: (status: boolean) => void,
    isFloating?: boolean,
    data: MenuItemType[]
}) {

    // const theme = useTheme()
    const router = useRouter()
    const currentPath = normalizeMenuPath(usePathname())

    const [activeMenu, setActiveMenu] = useState<null | MenuItemType>(null)
    const [open, setOpen] = useState(false)

    const handleCloseMenu = useCallback(() => {
        if (activeMenu) {
            setActiveMenu(null)
        } else {
            setOpen(false)
        }

    }, [activeMenu?.id])

    const handelClick = useCallback(() => {
        setActiveMenu(null)
        setOpen(false)
    }, [activeMenu?.id])

    const calcFromNow = useMemo(() => (datetime: string) => {
        return dayjs(datetime, 'YYYY-MM-DD HH:mm:ss').locale('fa').fromNow();
    }, [])

    const seoLinks = useMemo(() => {
        const links = new Map<string, string>();

        for (const menuItem of props.data) {
            if (menuItem.href) {
                links.set(menuItem.href, menuItem.label);
            }

            for (const col of menuItem.children ?? []) {
                for (const child of col.children ?? []) {
                    if ("href" in child && child.href) {
                        links.set(child.href, child.type === "link" ? child.label : menuItem.label);
                    }
                }
            }
        }

        return Array.from(links.entries()).map(([href, label]) => ({ href, label }));
    }, [props.data]);

    const renderSubMenu = useMemo(() => (item: SubMenuItem): ReactNode => {
        // red label marks the link of the page we are already on
        const isCurrent = !!item.href && normalizeMenuPath(item.href) === currentPath
        switch (item.type) {
            case "image":
                return (
                    <Grid key={item.id} component="li" size={12} sx={{
                        pt: 2,
                        '&:first-of-type': {
                            pt: 0
                        }
                    }}>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={handelClick} sx={{
                            display: 'block',
                            textDecoration: 'none',
                        }}>
                            {/* Without a width the media API hands back the untouched original:
                                these promos were arriving at 1920x1080 and 830KB each for a
                                380px box, before the menu was even opened. Sized for a 2x
                                phone, which is the only screen this menu renders on. */}
                            <MediaV2 media_id={item.media_id} alt={item.alt ?? "تصویر"} boxProps={{
                                className: 'image_media_content',
                                sx: {
                                    borderRadius: 1,
                                    aspectRatio: item.ratio ?? "2 / 1"
                                }
                            }}
                            height={450}
                            width={800}
                            options={{
                                quality: 80
                            }}
                            imageProps={{
                                fill: true,
                                style: {
                                    objectFit: 'cover'
                                }
                            }} />
                            {item.label && (
                                <Typography component="span" className="image_label" sx={{
                                    display: 'inline-block',
                                    fontSize: 13,
                                    color: isCurrent ? uiColors.primary : colors.grey[800],
                                    p: 1,

                                }}>{item.label}</Typography>
                            )}
                        </Box>
                    </Grid>
                )
            case "brand":
                return (
                    <Grid key={item.id} component="li" size={6} sx={{ maxHeight: 144 }}>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={handelClick} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            borderRadius: 1,
                            transition: '250ms',
                            // width: 144,
                            height: 95,
                            padding: 1,
                            opacity: 0.7,
                            '&:hover': {
                                opacity: 1,
                                backgroundColor: '#fff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }
                        }}>
                            <Media
                                id={item.media_id}
                                alt="تصویر برند"
                                // height={40}
                                width={132}
                                sx={{
                                    maxWidth: 96
                                }}
                                aspectRatio="1.5 / 1"
                                // width={140}
                                // fill
                                // action="thumbnail"
                                background="transparent"
                            />
                        </Box>
                    </Grid>
                )
            case "shape_type":
                return (
                    <Grid key={item.id} component="li" size={4} p={1}>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={handelClick} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            textDecoration: 'none',
                            borderRadius: 1,
                            transition: '250ms',
                            height: 92,
                            padding: 1,
                            border: '1px solid',
                            borderColor: colors.grey[300],

                            '&:hover': {
                                borderColor: 'transparent',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                bgcolor: '#fff',
                            }
                        }}>
                            <Media
                                id={item.media_id}
                                alt={item.label}
                                aspectRatio="2 / 1"
                                height={40}
                                width={80}
                                fill
                                action="thumbnail"
                                background="transparent"
                                sx={{ maxWidth: 80 }}
                            />
                            <Typography sx={{ mt: 1, color: isCurrent ? uiColors.primary : colors.grey[800], fontSize: 15 }}>{item.label}</Typography>
                        </Box>
                    </Grid>
                )
            case "link":
                return (
                    <Grid key={item.id} component="li" size={12}>
                        <FlexBox sx={{ justifyContent: 'space-between', px: 1 }} component={LinkWithoutPrefetch} href={item.href} onClick={handelClick}>
                            <Typography sx={{
                                color: isCurrent ? uiColors.primary : colors.grey[600],
                                textDecoration: 'none',
                                fontSize: 16,
                                height: 48,
                                // mb: 2,

                                py: 1,
                                display: 'block',
                                fontWeight: 400,
                            }} >{item.label}</Typography>
                        </FlexBox>
                    </Grid>

                )
            case "more_button":
                return (
                    <Grid key={item.id} component="li" size={12}>
                        <Box sx={{
                            color: item.text_color ?? "#242424",
                            textDecoration: 'none',
                            fontSize: 16,
                            borderBottom: '1px solid transparent',
                            transition: '100ms',
                            py: 1,
                            opacity: 0.8,
                            '& i': {
                                transition: '100ms',
                            },
                        }} component={LinkWithoutPrefetch} href={item.href} onClick={handelClick}>
                            <Typography sx={{ display: 'inline-block', mr: 1 }}>
                                {item.label}
                            </Typography>
                            <ChevronLeft color={item.text_color} size={18} />
                        </Box>
                    </Grid>

                )
            case "blog":
                return (
                    <Grid key={item.id} component="li" size={12} mb={2}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer',
                        }} component={LinkWithoutPrefetch} href={item.href} onClick={handelClick}>
                            <Media aspectRatio="2 / 1" width={120} id={item.media_id} alt={item.label} sx={{ overflow: 'hidden', borderRadius: '4px' }} />
                            <Box sx={{ flex: 1, ml: 2 }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 400, color: isCurrent ? uiColors.primary : "#212121" }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: 14, color: '#666666', mt: 1 }}>{calcFromNow(item.created_at)}</Typography>
                            </Box>
                        </Box>
                    </Grid>

                )
            case "wiki":
                return (
                    <Grid key={item.id} component="li" size={12} mb={2}>
                        <Box sx={{
                            display: 'block',
                            textDecoration: 'none',
                            borderLeft: '3px solid',
                            borderColor: colors.grey[300],
                            transition: 'border-color 200ms',
                            pl: 2,
                            cursor: 'pointer',
                        }} component={LinkWithoutPrefetch} href={item.href} onClick={handelClick}>
                            <Typography sx={{ fontSize: 16, fontWeight: 400, color: isCurrent ? uiColors.primary : "#212121" }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: 14, color: '#666666', mt: 1 }}>{item.description}</Typography>
                        </Box>
                    </Grid>

                )
            default: return <Fragment>unsupported type</Fragment>
        }
    }, [currentPath])

    const MobileMenuHeader = useMemo(() => (props: { menu?: MenuItemType }) => {
        return <FlexBox aria-label="mobile_header"
            sx={{
                height: 56,
                minHeight: 56,
                px: 1,
                borderBottom: '1px solid',
                borderColor: colors.grey[300],
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: '#fff'
            }}>
            <FlexBox >
                <IconButton
                    sx={{ height: 42, width: 42, position: 'relative' }}
                    onClick={handleCloseMenu}
                    aria-label={props.menu ? "بازگشت به منوی قبلی" : "بستن منو"}
                >
                    {props.menu ? (
                        <CenterBox sx={{ position: 'absolute', inset: 0 }}>
                            <ArrowRight />
                        </CenterBox>
                    ) : (
                        <CenterBox sx={{ position: 'absolute', inset: 0 }}>
                            <X />
                        </CenterBox>
                    )}
                </IconButton>
                {props.menu ? (
                    <Typography sx={{ fontWeight: 500, fontSize: 18, ml: 1 }}>
                        {props.menu?.label}
                    </Typography>
                ) : (
                    <Typography sx={{ fontWeight: 500, fontSize: 18, ml: 1 }}>
                        دسته بندی‌ها
                    </Typography>
                )}
            </FlexBox>
            {props.menu?.href && (
                <MuiLink component={LinkWithoutPrefetch} href={props.menu.href} onClick={handelClick} sx={{
                    fontSize: 12,
                    mr: 1,
                    display: 'inline-block',
                    py: 1,
                    textDecoration: 'none'
                }}>نمایش همه</MuiLink>
            )}
            {/* {!props.menu && (
                <SearchButton />
            )} */}
        </FlexBox>
    }, [activeMenu?.id])

    const handlePickMenu = useCallback((menu: MenuItemType) => () => {
        if (menu.children?.length) {
            setActiveMenu(menu)
        } else {
            router.push(menu.href)
        }
    }, [])


    return (
        <>
            <SwipeableDrawer
                keepMounted
                variant="temporary"
                slotProps={{
                    paper: {
                        sx: { left: 0 }

                    },
                    swipeArea: {
                        sx: {
                            left: 0
                        }
                    }
                }}
                anchor={'right'}
                open={open}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
            >
                <MobileMenuHeader />

                <Box sx={{ position: "relative", height: '100%', p: 1 }}>
                    {/* <TransitionGroup > */}
                    {props.data.map((menuItem, index) => {
                        const showSubmenu = activeMenu?.id === menuItem.id;
                        return (
                            <Fragment key={index}>

                                <ButtonBase sx={{
                                    justifyContent: 'space-between',
                                    width: 1,
                                    height: 48,
                                    // mx: 1,
                                    pr: 0,
                                    pl: 2,
                                    // my: 1,
                                }} onClick={handlePickMenu(menuItem)}>
                                    <Typography component="span" sx={{
                                        fontFamily: 'font',
                                    }}>{menuItem.label}</Typography>
                                    <CenterBox sx={{ height: 42, width: 42 }}>
                                        {menuItem.children?.length ? (
                                            <ChevronLeft size={18} />
                                        ) : (
                                            <Fragment />
                                        )}
                                    </CenterBox>
                                </ButtonBase>

                                {index + 1 !== props.data.length && (
                                    <Divider color={colors.grey[300]} sx={{ my: 1, mx: 1 }} />
                                )}
                                {menuItem.children && (
                                    <SwipeableDrawer
                                        keepMounted
                                        variant="temporary"
                                        // hideBackdrop
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    left: 0
                                                }
                                            }
                                        }}
                                        anchor={'right'}
                                        open={showSubmenu}
                                        onClose={() => setActiveMenu(null)}
                                        onOpen={() => { }}
                                    >
                                        <MobileMenuHeader menu={menuItem} />

                                        <Box sx={{ p: 2 }}>
                                            {menuItem.children?.map((child1, key2) => (
                                                <Box key={key2}>
                                                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 18, mb: 2, color: colors.grey[800] }}> {child1.label} </Typography>
                                                    <Grid container component="ul" sx={{ listStyle: 'none', pl: 0 }} spacing={0}>
                                                        {child1.children.map((child2) => renderSubMenu(child2))}
                                                    </Grid>
                                                    {key2 + 1 !== menuItem.children.length && (
                                                        <Divider color={colors.grey[300]} sx={{ mb: 2, mt: 1 }} />
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </SwipeableDrawer>
                                )}
                            </Fragment>
                        )
                    })}
                    {/* </TransitionGroup> */}
                </Box>

            </SwipeableDrawer>
            <IconButton sx={{ height: 42, width: 42 }} onClick={() => {
                setOpen(true)
            }} aria-label="باز کردن منوی دسته‌بندی‌ها">
                <MenuIcon />
            </IconButton>

            <Box
                component="nav"
                aria-label="mobile seo links"
                sx={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    p: 0,
                    m: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    border: 0,
                    whiteSpace: "nowrap",
                }}
            >
                {seoLinks.map((item) => (
                    <MuiLink key={item.href} component={LinkWithoutPrefetch} href={item.href}>
                        {item.label}
                    </MuiLink>
                ))}
            </Box>
        </>
    )
}
